import { Helmet } from "react-helmet-async";

/**
 * Per-page <head> tags: browser tab title plus Open Graph / Twitter meta.
 *
 * NOTE ON LINK PREVIEWS
 * ---------------------
 * WhatsApp / Facebook / X crawlers do not execute JavaScript, so what they
 * read is the raw index.html, never what this component renders. A link
 * shared to WhatsApp therefore shows the default tags from public/index.html,
 * NOT the per-item values set here.
 *
 * What this component does give you: the correct browser tab title for real
 * visitors, and correct per-page metadata for crawlers that do run the bundle
 * (Google). Per-item previews on WhatsApp would need the tags injected
 * server-side for crawler requests.
 */

export const SITE_NAME = "جمعية تكافل لرعاية الأيتام";

const DEFAULT_DESCRIPTION =
  "جمعية تكافل لرعاية الأيتام جمعية أهلية بمنطقة المدينة المنورة، تهدف إلى بناء أنموذج مؤسسي ريادي في رعاية وتنمية اليتيم بالشراكة الفاعلة مع الداعمين وباحترافية عالية من العاملين في الجمعية.";

const DEFAULT_IMAGE = `${window.location.origin}/og-image.png`;

interface SeoProps {
  /** Page/item name. Rendered as "<title> | جمعية تكافل لرعاية الأيتام". */
  title?: string;
  /** Plain-text summary. HTML from the API is stripped before use. */
  description?: string;
  /** Absolute image URL, e.g. project?.title_image_ids?.[0]. */
  image?: string;
  /** Canonical URL. Defaults to the current location. */
  url?: string;
}

/** API description fields may contain Odoo HTML; previews need plain text. */
const toPlainText = (value?: string, maxLength = 200) => {
  if (!value) return "";
  const text = value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > maxLength
    ? `${text.slice(0, maxLength - 1).trimEnd()}…`
    : text;
};

const Seo = ({ title, description, image, url }: SeoProps) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const metaDescription = toPlainText(description) || DEFAULT_DESCRIPTION;
  const metaImage = image || DEFAULT_IMAGE;
  const metaUrl = url || window.location.href;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={metaUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="ar_AR" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:secure_url" content={metaImage} />
      <meta property="og:image:alt" content={title || SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
    </Helmet>
  );
};

export default Seo;
